import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (!user.isAdmin) {
      navigate('/login');
      return;
    }

    fetchStats();
  }, [navigate]);

  const fetchStats = async () => {
    try {
      const res = await api.get('/admin/dashboard/stats');
      setStats(res.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      if (error.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="container mt-5">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-4">
      <div className="row mb-4">
        <div className="col">
          <h2>Admin Dashboard</h2>
          <p className="text-muted">Welcome to InternStacks Admin Panel</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="row g-3 mb-4">
        <div className="col-md-3">
          <div className="card border-primary">
            <div className="card-body">
              <h6 className="card-subtitle mb-2 text-muted">Total Programs</h6>
              <h3 className="card-title">{stats?.programs?.total || 0}</h3>
              <small className="text-success">{stats?.programs?.active || 0} Active</small>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-success">
            <div className="card-body">
              <h6 className="card-subtitle mb-2 text-muted">Total Students</h6>
              <h3 className="card-title">{stats?.students?.total || 0}</h3>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-info">
            <div className="card-body">
              <h6 className="card-subtitle mb-2 text-muted">Total Enrollments</h6>
              <h3 className="card-title">{stats?.enrollments?.total || 0}</h3>
              <small className="text-info">{stats?.enrollments?.active || 0} Active</small>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-warning">
            <div className="card-body">
              <h6 className="card-subtitle mb-2 text-muted">Completed</h6>
              <h3 className="card-title">{stats?.enrollments?.completed || 0}</h3>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="row mb-4">
        <div className="col">
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">Quick Actions</h5>
            </div>
            <div className="card-body">
              <div className="btn-group" role="group">
                <button 
                  className="btn btn-primary"
                  onClick={() => navigate('/admin/programs')}
                >
                  Manage Programs
                </button>
                <button 
                  className="btn btn-success"
                  onClick={() => navigate('/admin/assessments')}
                >
                  Manage Assessments
                </button>
                <button 
                  className="btn btn-info"
                  onClick={() => navigate('/admin/content')}
                >
                  Manage Content
                </button>
                <button 
                  className="btn btn-warning"
                  onClick={() => navigate('/admin/enrollments')}
                >
                  View Enrollments
                </button>
                <button 
                  className="btn btn-primary"
                  onClick={() => navigate('/admin/internships')}
                >
                  Manage Internships
                </button>
                <button 
                  className="btn btn-secondary"
                  onClick={() => navigate('/admin/students')}
                >
                  View Students
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Enrollments */}
      <div className="row">
        <div className="col">
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">Recent Enrollments</h5>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th>Student</th>
                      <th>Program</th>
                      <th>Status</th>
                      <th>Progress</th>
                      <th>Enrolled</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats?.recentEnrollments?.map((enrollment) => (
                      <tr key={enrollment._id}>
                        <td>{enrollment.user?.name}</td>
                        <td>{enrollment.program?.title}</td>
                        <td>
                          <span className={`badge bg-${
                            enrollment.enrollmentStatus === 'completed' ? 'success' :
                            enrollment.enrollmentStatus === 'active' ? 'primary' :
                            'secondary'
                          }`}>
                            {enrollment.enrollmentStatus}
                          </span>
                        </td>
                        <td>{enrollment.overallProgress || 0}%</td>
                        <td>{new Date(enrollment.createdAt).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Category Distribution */}
      <div className="row mt-4">
        <div className="col-md-6">
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">Students by Category</h5>
            </div>
            <div className="card-body">
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th>Count</th>
                  </tr>
                </thead>
                <tbody>
                  {stats?.students?.byCategory?.map((cat) => (
                    <tr key={cat._id}>
                      <td><span className="badge bg-secondary">{cat._id}</span></td>
                      <td>{cat.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">Enrollments by Status</h5>
            </div>
            <div className="card-body">
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th>Status</th>
                    <th>Count</th>
                  </tr>
                </thead>
                <tbody>
                  {stats?.enrollments?.byStatus?.map((status) => (
                    <tr key={status._id}>
                      <td><span className="badge bg-info">{status._id}</span></td>
                      <td>{status.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;
